// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {EVERYONE_MENTION, HERE_MENTION, MENTIONS_LABEL} from '@app/features/app/config/I18nDisplayConstants';
import Authentication from '@app/features/auth/state/Authentication';
import Channels from '@app/features/channel/state/Channels';
import * as ChannelUtils from '@app/features/channel/utils/ChannelUtils';
import styles from '@app/features/guild/components/modals/GuildNotificationSettingsModal.module.css';
import Guilds from '@app/features/guild/state/Guilds';
import * as GuildMemberCommands from '@app/features/member/commands/GuildMemberCommands';
import GuildMembers from '@app/features/member/state/GuildMembers';
import {Button} from '@app/features/ui/button/Button';
import {Checkbox} from '@app/features/ui/checkbox/Checkbox';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {Select} from '@app/features/ui/components/form/Select';
import {Switch} from '@app/features/ui/components/form/Switch';
import {RadioGroup, type RadioOption} from '@app/features/ui/radio_group/RadioGroup';
import * as UserGuildSettingsCommands from '@app/features/user/commands/UserGuildSettingsCommands';
import UserGuildSettings from '@app/features/user/state/UserGuildSettings';
import {ChannelTypes} from '@app/vendor/constants/ChannelConstants';
import {MessageNotifications} from '@app/vendor/constants/NotificationConstants';
import {MentionReplyPreferences} from '@app/vendor/constants/UserConstants';
import type {ChannelId} from '@app/vendor/schema/branded/WireIds';
import {useLingui} from '@lingui/react/macro';
import {FolderIcon, XIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';

interface ChannelOption {
	value: string;
	label: string;
	icon: React.ReactNode;
	categoryName?: string;
	isCategory: boolean;
}
export const GuildNotificationSettingsModal = observer(({guildId}: {guildId: string}) => {
	const {t} = useLingui();
	const guild = Guilds.getGuild(guildId);
	const settings = UserGuildSettings.getSettings(guildId);
	if (!guild || !settings) return null;
	const channels = Channels.getGuildChannels(guildId);
	const categories = channels.filter((c) => c.type === ChannelTypes.GUILD_CATEGORY);
	const channelOptions: Array<ChannelOption> = [
		...categories.map((cat) => ({
			value: cat.id,
			label: cat.name || '',
			icon: <FolderIcon size={16} className={styles.iconTertiary} />,
			isCategory: true,
		})),
		...channels
			.filter((c) => c.type !== ChannelTypes.GUILD_CATEGORY)
			.map((ch) => {
				const category = ch.parentId ? categories.find((c) => c.id === ch.parentId) : null;
				return {
					value: ch.id,
					label: ch.name || '',
					icon: ChannelUtils.getIcon(ch, {size: 16, className: styles.iconTertiary}),
					categoryName: category?.name ?? undefined,
					isCategory: false,
				};
			}),
	];
	const selectOptions = channelOptions.map((option) => ({
		value: option.value,
		label: option.label,
		isDisabled: false,
	}));
	const notificationOptions: Array<RadioOption<number>> = [
		{
			value: MessageNotifications.ALL_MESSAGES,
			name: t`All Messages`,
		},
		{
			value: MessageNotifications.ONLY_MENTIONS,
			name: t`Only Mentions`,
		},
		{
			value: MessageNotifications.NO_MESSAGES,
			name: t`Nothing`,
		},
	];
	const unreadBadgesOptions: Array<RadioOption<number>> = [
		{
			value: MessageNotifications.INHERIT,
			name: t`Default`,
		},
		{
			value: MessageNotifications.ALL_MESSAGES,
			name: t`All Messages`,
		},
		{
			value: MessageNotifications.ONLY_MENTIONS,
			name: t`Only Mentions`,
		},
		{
			value: MessageNotifications.NO_MESSAGES,
			name: t`Nothing`,
		},
	];
	const guildNotificationLevel = UserGuildSettings.getGuildMessageNotifications(guildId);
	const guildUnreadBadgesValue = settings.unread_badges == null ? MessageNotifications.INHERIT : settings.unread_badges;
	const currentMember = GuildMembers.getMember(guildId, Authentication.currentUserId);
	const memberMentionFlags = currentMember?.mentionFlags ?? MentionReplyPreferences.NO_PREFERENCE;
	const mentionPreferenceOptions: Array<RadioOption<number>> = [
		{
			value: MentionReplyPreferences.NO_PREFERENCE,
			name: t`Inherit from account`,
		},
		{
			value: MentionReplyPreferences.PREFER_MENTION,
			name: t`Prefer @mention`,
		},
		{
			value: MentionReplyPreferences.PREFER_NO_MENTION,
			name: t`Prefer no @mention`,
		},
	];
	const handleMentionPreferenceChange = (value: number) => {
		void GuildMemberCommands.updateProfile(guildId, {mention_flags: value});
	};
	const handleAddOverride = (value: string | null) => {
		if (!value) return;
		const existingOverride = settings.channel_overrides?.[value as ChannelId];
		if (existingOverride) {
			return;
		}
		UserGuildSettingsCommands.updateChannelOverride(guildId, value, {
			message_notifications: MessageNotifications.INHERIT,
			muted: false,
		});
	};
	const handleRemoveOverride = (channelId: string) => {
		UserGuildSettingsCommands.updateChannelOverride(guildId, channelId, null);
	};
	const handleOverrideNotificationChange = (channelId: string, level: number) => {
		UserGuildSettingsCommands.updateChannelOverride(guildId, channelId, {
			message_notifications: level,
		});
	};
	const handleOverrideMuteChange = (channelId: string, muted: boolean) => {
		UserGuildSettingsCommands.updateChannelOverride(guildId, channelId, {
			muted,
		});
	};
	const overrideChannels = settings.channel_overrides
		? Object.entries(settings.channel_overrides)
				.map(([channelId, override]) => {
					const channel = Channels.getChannel(channelId);
					const category = channel?.parentId ? Channels.getChannel(channel.parentId) : null;
					const isCategory = channel?.type === ChannelTypes.GUILD_CATEGORY;
					return {
						channelId,
						override,
						channel,
						category,
						isCategory,
					};
				})
				.sort((a, b) => {
					if (!a.channel && !b.channel) return 0;
					if (!a.channel) return 1;
					if (!b.channel) return -1;
					const posA = a.channel.position ?? 0;
					const posB = b.channel.position ?? 0;
					if (posA !== posB) {
						return posA - posB;
					}
					return a.channelId.localeCompare(b.channelId);
				})
		: [];
	return (
		<Modal.Root size="medium">
			<Modal.Header title={t`Notification Settings`} />
			<Modal.Content>
				<div className={styles.container}>
					<div className={styles.section}>
						<Switch
							label={t`Mute ${guild.name}`}
							description={t`Muting a community prevents unread indicators and notifications from appearing unless you are mentioned`}
							value={settings.muted}
							onChange={(value) => UserGuildSettingsCommands.updateGuildSettings(guildId, {muted: value})}
						/>
					</div>
					<div className={styles.notificationSection}>
						<h3 className={styles.sectionTitle}>{t`Community Notification Settings`}</h3>
						<RadioGroup
							options={notificationOptions}
							value={guildNotificationLevel}
							onChange={(value) =>
								UserGuildSettingsCommands.updateGuildSettings(guildId, {message_notifications: value})
							}
							aria-label={t`Community notification level`}
						/>
					</div>
					<div className={styles.notificationSection}>
						<h3 className={styles.sectionTitle}>{t`Unread Badges`}</h3>
						<p className={styles.sectionDescription}>
							{t`Choose when this community shows an unread badge. "Default" follows notifications and respects mute.`}
						</p>
						<RadioGroup
							options={unreadBadgesOptions}
							value={guildUnreadBadgesValue}
							onChange={(value) =>
								UserGuildSettingsCommands.updateUnreadBadgesLevel(
									guildId,
									value === MessageNotifications.INHERIT ? null : value,
								)
							}
							aria-label={t`Community unread badges level`}
						/>
					</div>
					<div className={styles.suppressSection}>
						<Switch
							label={t`Suppress ${EVERYONE_MENTION} and ${HERE_MENTION}`}
							value={settings.suppress_everyone}
							onChange={(value) => UserGuildSettingsCommands.updateGuildSettings(guildId, {suppress_everyone: value})}
						/>
						<Switch
							label={t`Suppress All Role ${MENTIONS_LABEL}`}
							value={settings.suppress_roles}
							onChange={(value) => UserGuildSettingsCommands.updateGuildSettings(guildId, {suppress_roles: value})}
						/>
					</div>
					<div className={styles.notificationSection}>
						<h3 className={styles.sectionTitle}>{t`Reply Mention Preference`}</h3>
						<p className={styles.sectionDescription}>
							{t`Override how replies to your messages handle the @ mention in this community. "Inherit from account" uses your account-wide setting.`}
						</p>
						<RadioGroup
							options={mentionPreferenceOptions}
							value={memberMentionFlags}
							onChange={handleMentionPreferenceChange}
							aria-label={t`Reply mention preference for this community`}
						/>
					</div>
					<div className={styles.mobilePushSection}>
						<Switch
							label={t`Mobile Push Notifications`}
							value={settings.mobile_push}
							onChange={(value) => UserGuildSettingsCommands.updateGuildSettings(guildId, {mobile_push: value})}
						/>
					</div>
					<div className={styles.overridesSection}>
						<h3 className={styles.sectionTitle}>{t`Notification Overrides`}</h3>
						<Select<string | null>
							value={null}
							options={selectOptions}
							onChange={handleAddOverride}
							placeholder={t`Select a channel or category`}
						/>
						{overrideChannels.length > 0 && (
							<div className={styles.overridesSection}>
								<div className={styles.overridesHeader}>
									<div className={styles.overridesHeaderCellLeft}>{t`Channel or Category`}</div>
									<div className={styles.overridesHeaderCell}>{t`All`}</div>
									<div className={styles.overridesHeaderCell}>{t`Mentions`}</div>
									<div className={styles.overridesHeaderCell}>{t`Nothing`}</div>
									<div className={styles.overridesHeaderCellMute}>{t`Mute`}</div>
								</div>
								{overrideChannels.map(({channelId, override, channel, category, isCategory}) => {
									if (!channel) return null;
									const notifLevel = override.message_notifications ?? MessageNotifications.INHERIT;
									const isAll = notifLevel === MessageNotifications.ALL_MESSAGES;
									const isMentions = notifLevel === MessageNotifications.ONLY_MENTIONS;
									const isNothing = notifLevel === MessageNotifications.NO_MESSAGES;
									const isInherit = notifLevel === MessageNotifications.INHERIT;
									const resolvedLevel = isInherit ? guildNotificationLevel : notifLevel;
									return (
										<div key={channelId} className={styles.overrideItem}>
											<div className={styles.overrideHeader}>
												<div className={styles.channelInfo}>
													{isCategory ? (
														<FolderIcon size={20} className={styles.channelIcon} />
													) : (
														ChannelUtils.getIcon(channel, {
															size: 20,
															className: styles.channelIcon,
														})
													)}
													<div className={styles.channelDetails}>
														<span className={styles.channelName}>{channel.name ?? ''}</span>
														{!isCategory && (
															<span className={styles.categoryName}>
																{category ? (category.name ?? '') : t`No Category`}
															</span>
														)}
													</div>
												</div>
												<button
													type="button"
													onClick={() => handleRemoveOverride(channelId)}
													className={styles.removeButton}
													aria-label={t`Remove Override`}
												>
													<XIcon size={14} weight="bold" />
												</button>
											</div>
											<div className={styles.mobileOverrideOptions}>
												<Switch
													label={t`All Messages`}
													value={isAll || (isInherit && resolvedLevel === MessageNotifications.ALL_MESSAGES)}
													onChange={() =>
														handleOverrideNotificationChange(channelId, MessageNotifications.ALL_MESSAGES)
													}
													compact
												/>
												<Switch
													label={t`Only Mentions`}
													value={isMentions || (isInherit && resolvedLevel === MessageNotifications.ONLY_MENTIONS)}
													onChange={() =>
														handleOverrideNotificationChange(channelId, MessageNotifications.ONLY_MENTIONS)
													}
													compact
												/>
												<Switch
													label={t`Nothing`}
													value={isNothing || (isInherit && resolvedLevel === MessageNotifications.NO_MESSAGES)}
													onChange={() => handleOverrideNotificationChange(channelId, MessageNotifications.NO_MESSAGES)}
													compact
												/>
												<Switch
													label={t`Mute Channel`}
													value={override.muted}
													onChange={(checked) => handleOverrideMuteChange(channelId, checked)}
													compact
												/>
											</div>
											<div className={styles.desktopNotificationOptions}>
												<div className={styles.checkboxCell}>
													<Checkbox
														checked={isAll || (isInherit && resolvedLevel === MessageNotifications.ALL_MESSAGES)}
														onChange={() =>
															handleOverrideNotificationChange(channelId, MessageNotifications.ALL_MESSAGES)
														}
														aria-label={t`All Messages`}
													/>
												</div>
												<div className={styles.checkboxCell}>
													<Checkbox
														checked={isMentions || (isInherit && resolvedLevel === MessageNotifications.ONLY_MENTIONS)}
														onChange={() =>
															handleOverrideNotificationChange(channelId, MessageNotifications.ONLY_MENTIONS)
														}
														aria-label={t`Only Mentions`}
													/>
												</div>
												<div className={styles.checkboxCell}>
													<Checkbox
														checked={isNothing || (isInherit && resolvedLevel === MessageNotifications.NO_MESSAGES)}
														onChange={() =>
															handleOverrideNotificationChange(channelId, MessageNotifications.NO_MESSAGES)
														}
														aria-label={t`Nothing`}
													/>
												</div>
												<div className={styles.checkboxCell}>
													<Checkbox
														checked={override.muted}
														onChange={(checked) => handleOverrideMuteChange(channelId, checked)}
														aria-label={t`Mute Channel`}
													/>
												</div>
											</div>
										</div>
									);
								})}
							</div>
						)}
					</div>
				</div>
			</Modal.Content>
			<Modal.Footer>
				<Button onClick={() => ModalCommands.pop()}>{t`Done`}</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
